import React, {useCallback, useMemo} from 'react'
import {Box, Checkbox, Stack} from '@chakra-ui/react'
import PropTypes from 'prop-types'

/**
 * Renders a single checkbox item for a checkbox filter.
 * @param {any} [filterId]
 * @param {{label: string, value: any}} option
 * @param {array} [selectedOptions]
 * @param {function} [_onChange]
 * @returns {JSX.Element}
 * @constructor
 */
const CheckboxFilterItem = ({filterId, option, selectedOptions, onChange: _onChange}) => {
    const isChecked = useMemo(
        () => selectedOptions?.includes(option.value),
        [selectedOptions, option.value]
    )
    const onChange = useCallback(() => {
        _onChange?.(option, {
            filterId,
            select: !isChecked,
            multiple: true
        })
    }, [filterId, isChecked, option, _onChange])

    return (
        <Box>
            <Checkbox
                // The following `false` fallback is required to avoid the checkbox
                // from switching to "uncontrolled mode" when `isChecked` is empty.
                isChecked={isChecked ?? false}
                onChange={onChange}
            >
                {option.label}
            </Checkbox>
        </Box>
    )
}

CheckboxFilterItem.propTypes = {
    filterId: PropTypes.any,
    option: PropTypes.object.isRequired,
    selectedOptions: PropTypes.array,
    onChange: PropTypes.func
}

/**
 * Renders a checkbox filter. Based on the original CheckboxRefinements component.
 * @param {any} [filterId]
 * @param {array} [options]
 * @param {array} [selectedOptions]
 * @param {function} [onChange]
 * @returns {JSX.Element|null}
 * @constructor
 */
const CheckboxFilter = ({filterId, options, selectedOptions, onChange}) => {
    const displayedOptions = useMemo(
        () => options?.filter((option) => option.hitCount == null || option.hitCount > 0),
        [options]
    )

    if (!displayedOptions?.length) {
        return null
    }

    return (
        <Stack spacing={4}>
            {displayedOptions.map((option) => (
                <CheckboxFilterItem
                    key={option.value}
                    filterId={filterId}
                    option={option}
                    selectedOptions={selectedOptions}
                    onChange={onChange}
                />
            ))}
        </Stack>
    )
}

CheckboxFilter.propTypes = {
    filterId: PropTypes.any,
    options: PropTypes.array,
    selectedOptions: PropTypes.array,
    onChange: PropTypes.func
}

export default CheckboxFilter
